import {Component, OnInit} from '@angular/core';
import {RoutesEnum} from '../../../core/services/navigation/routes.enum';
import {FormBuilder, FormControl, Validators} from '@angular/forms';
import {Utils} from '@astek/utils';
import {ApiCreateEditHealthCenterRequest} from '../services/http/api-create-edit-health-center-request';
import {filter, finalize, map, Observable, startWith, tap} from 'rxjs';
import {ApiCreateEditHealthCenterResponse} from '../services/http/api-create-edit-health-center-response';
import {HealthCenterRestApiService} from '../services/http/health-center-rest-api.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {TranslocoService} from '@jsverse/transloco';
import {NavigationService} from '../../../core/services/navigation/navigation.service';
import {ApiHealthCenterResponse} from '../services/http/api-health-center-response';
import {ActivatedRoute} from '@angular/router';
import {CountryRestApiService} from '../../../core/services/country/http/country-rest-api.service';
import {CountryService} from '../../../core/services/country/country.service';
import {Country} from '../../../core/services/country/country';
import {ApiCountriesListResponse} from '../../../core/services/country/http/api-countries-list-response';
import {ApiCountryResponse} from '../../../core/services/country/http/api-country-response';
import {healthCenterCountryValidator} from '../services/validator/health-center-country-validator';

@Component({
  selector: 'xp-create-edit-health-center',
  templateUrl: './create-edit-health-center.component.html',
  styleUrls: ['../../crud.common.scss', './create-edit-health-center.component.scss'],
})
export class CreateEditHealthCenterComponent implements OnInit {

  public readonly formGroup = this._formBuilder.group({
    name: new FormControl<string>('', Validators.required.bind(Validators)),
    address: new FormControl<string | null>(''),
    zipCode: new FormControl<string | null>(''),
    city: new FormControl<string | null>(''),
    country: new FormControl<Country | null>(null),
    contactFirstName: new FormControl<string | null>(''),
    contactLastName: new FormControl<string | null>(''),
    email: new FormControl<string>('', [
      Validators.required.bind(Validators),
      Validators.email.bind(Validators),
    ]),
    phoneNumber: new FormControl<string>(''),
  }, {validators: [healthCenterCountryValidator]});

  public isLoading: boolean = false;
  public errorKey: string = '';
  public countries: Array<Country> = [];
  public filteredCountries: Array<Country> = [];

  private _healthCenter: ApiHealthCenterResponse | null = null;

  constructor(
    private readonly _formBuilder: FormBuilder,
    private readonly _healthCenterRestApiService: HealthCenterRestApiService,
    private readonly _snackBar: MatSnackBar,
    private readonly _translocoService: TranslocoService,
    private readonly _navigationService: NavigationService,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _countryRestApiService: CountryRestApiService,
    private readonly _countryService: CountryService,
  ) {
    this._activatedRoute.data.subscribe(({healthCenter}) => {
      this._healthCenter = healthCenter as ApiHealthCenterResponse;
    });
  }

  public get healthCenterListRoute(): Array<string> {
    return ['/', RoutesEnum.HEALTH_CENTERS];
  }

  public get isEdit(): boolean {
    return Utils.isNotNullOrUndefined(this._healthCenter);
  }

  public ngOnInit(): void {
    this.initCountries().subscribe({
      next: () => this.initForm(),
      error: () => this.handleError(),
      complete: () => this.isLoading = false,
    });
  }

  public countryToString(country: Country | null): string {
    if (Utils.isNullOrUndefined(country)) {
      return '';
    }
    return country.toString();
  }

  public onSubmit(): void {
    if (this.formGroup.invalid ||
      Utils.isBlank(this.formGroup.controls.name.value) ||
      Utils.isBlank(this.formGroup.controls.email.value)
    ) {
      return;
    }
    this.isLoading = true;
    this.errorKey = '';

    let country: string | null = null;
    if (Utils.isNotNullOrUndefined(this.formGroup.controls.country.value)) {
      country = (this.formGroup.controls.country.value).alpha2Code;
    }

    const data: ApiCreateEditHealthCenterRequest = {
      name: this.formGroup.controls.name.value,
      address: this.formGroup.controls.address.value,
      zipCode: this.formGroup.controls.zipCode.value,
      city: this.formGroup.controls.city.value,
      country: country,
      contactFirstName: this.formGroup.controls.contactFirstName.value,
      contactLastName: this.formGroup.controls.contactLastName.value,
      email: this.formGroup.controls.email.value,
      phoneNumber: this.formGroup.controls.phoneNumber.value,
    };
    this.requestCreateOrUpdate(data).pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: async () => {
          this._snackBar.open(
            this._translocoService.translate(
              this.isEdit ? 'HEALTH_CENTERS.CREATE_EDIT.SUCCESS_EDIT' : 'HEALTH_CENTERS.CREATE_EDIT.SUCCESS_CREATE',
            ),
            '',
            {duration: 7000},
          );
          await this._navigationService.navigateTo(RoutesEnum.HEALTH_CENTERS);
        },
        error: () => this.handleError(),
      });
  }

  private initCountries(): Observable<Array<Country>> {
    this.formGroup.controls.country.valueChanges.pipe(
      startWith(''),
      filter((value: any) => typeof value === 'string'),
      map((value: string) => this._countryService.filterCountryList(this.countries, value ?? '')),
    ).subscribe((countries: Array<Country>) => this.filteredCountries = countries);
    return this._countryRestApiService.requestCountryList().pipe(
      map((response: ApiCountriesListResponse) => response.countries),
      map((countries: Array<ApiCountryResponse>) => countries.map(
        (message: ApiCountryResponse) => (new Country()).fromMessage(message),
      )),
      tap((countries: Array<Country>) => this.countries = countries),
    );
  }

  private initForm(): void {
    if (Utils.isNullOrUndefined(this._healthCenter)) {
      return;
    }
    const country = this._healthCenter.country
      ? this._countryService.findByAlpha2CodeCountry(this.countries, this._healthCenter.country)
      : null;
    console.log({country: country, countries: this.countries, countryName: this._healthCenter.country});
    this.formGroup.controls.name.setValue(this._healthCenter.name);
    this.formGroup.controls.address.setValue(this._healthCenter.address);
    this.formGroup.controls.zipCode.setValue(this._healthCenter.zipCode);
    this.formGroup.controls.city.setValue(this._healthCenter.city);
    this.formGroup.controls.country.setValue(country);
    this.formGroup.controls.contactFirstName.setValue(this._healthCenter.contactFirstName);
    this.formGroup.controls.contactLastName.setValue(this._healthCenter.contactLastName);
    this.formGroup.controls.email.setValue(this._healthCenter.email);
    this.formGroup.controls.phoneNumber.setValue(this._healthCenter.phoneNumber);
  }

  private requestCreateOrUpdate(
    data: ApiCreateEditHealthCenterRequest,
  ): Observable<ApiCreateEditHealthCenterResponse | void> {
    if (Utils.isNotNullOrUndefined(this._healthCenter)) {
      return this._healthCenterRestApiService.requestUpdate(this._healthCenter.id, data);
    }
    return this._healthCenterRestApiService.requestCreate(data);
  }

  private handleError(): void {
    this.errorKey = 'SHARED.ERRORS.UNKNOWN';
  }
}
